import { useState } from 'react';
import { Package, Truck, CheckCircle, Clock, PlayCircle, StopCircle } from 'lucide-react';

const Unloading = () => {
  const [operations, setOperations] = useState([
    { id: 1, truck: 'MH-12-AB-1234', dock: 'DOCK-01', status: 'IN_PROGRESS', progress: 65, startTime: '09:30 AM', materials: 'Raw Materials' },
    { id: 2, truck: 'KA-05-XY-5678', dock: 'DOCK-03', status: 'IN_PROGRESS', progress: 30, startTime: '10:15 AM', materials: 'Components' },
    { id: 3, truck: 'TN-22-CD-9012', dock: 'DOCK-05', status: 'COMPLETED', progress: 100, startTime: '08:00 AM', materials: 'Packaging' },
  ]);

  const handleUpdateProgress = (id, newProgress) => {
    setOperations(operations.map(op =>
      op.id === id ? { ...op, progress: Math.min(100, newProgress), status: newProgress >= 100 ? 'COMPLETED' : 'IN_PROGRESS' } : op
    ));
  };

  const handleCompleteOperation = (id) => {
    setOperations(operations.map(op =>
      op.id === id ? { ...op, progress: 100, status: 'COMPLETED' } : op
    ));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Unloading Operations</h1>
        <p className="text-gray-600 mt-2">Track and manage material unloading processes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Active Operations</p>
              <p className="text-3xl font-bold mt-1">{operations.filter(o => o.status === 'IN_PROGRESS').length}</p>
            </div>
            <Clock className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        <div className="card bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Completed Today</p>
              <p className="text-3xl font-bold mt-1">{operations.filter(o => o.status === 'COMPLETED').length}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>
        <div className="card bg-purple-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Avg Progress</p>
              <p className="text-3xl font-bold mt-1">
                {Math.round(operations.reduce((acc, op) => acc + op.progress, 0) / operations.length)}%
              </p>
            </div>
            <Package className="w-10 h-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Operations List */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Current Operations</h2>
        <div className="space-y-4">
          {operations.map((op) => (
            <div key={op.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-4">
                  <Truck className="w-6 h-6 text-nexgensis-primary" />
                  <div>
                    <p className="font-semibold">{op.truck}</p>
                    <p className="text-sm text-gray-600">{op.dock} • {op.materials} • Started: {op.startTime}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded text-sm font-medium ${
                  op.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                  op.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {op.status.replace('_', ' ')}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{op.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-nexgensis-primary h-3 rounded-full transition-all duration-300"
                    style={{width: `${op.progress}%`}}
                  ></div>
                </div>
              </div>

              {/* Action Buttons */}
              {op.status !== 'COMPLETED' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleUpdateProgress(op.id, op.progress + 10)}
                    className="btn-secondary text-xs flex items-center"
                  >
                    <PlayCircle className="w-4 h-4 mr-1" />
                    +10% Progress
                  </button>
                  <button
                    onClick={() => handleCompleteOperation(op.id)}
                    className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 flex items-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Complete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Unloading;
